import type { canvas } from '@bb/contracts';

/**
 * Editing commands of the Canvas graph (BB-114). Each takes a draft and returns a new one (the history keeps the old);
 * layout positions live next to the semantics (`layout`) and never change what runs.
 */
export type Draft = canvas.CanvasDefinition;
export type Step = canvas.Step;
export type Position = { x: number; y: number };
export type StepTypeMeta = Pick<canvas.CanvasStepTypeView, 'type' | 'label' | 'defaultConfig' | 'defaultPorts'>;
export type IdFactory = (prefix: 's' | 'v') => string;
export type Where = { kind: 'free'; position?: Position } | { kind: 'after_step'; stepId: string; port: string } | { kind: 'after_variant'; variantId: string };
export interface Fragment {
  steps: Step[];
  layout: Record<string, Position>;
}

/** Layout key of a variant node (layout keys are step ids otherwise). */
export const variantKey = (variantId: string) => `variant__${variantId}`;
export const newNodeId: IdFactory = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const ROW = 140;
const clone = <T>(value: T): T => structuredClone(value);
const withLayout = (d: Draft, key: string, position: Position | undefined): Draft => (position ? { ...d, layout: { ...(d.layout ?? {}), [key]: position } } : d);
const below = (p: Position | undefined, rows = 1): Position | undefined => (p ? { x: p.x, y: p.y + ROW * rows } : undefined);

/** Adds a step of a type with its default config; on an edge it is inserted, keeping what followed. */
export function addStep(d: Draft, meta: StepTypeMeta, where: Where, id: string): Draft {
  const next = clone(d);
  const step: Step = { id, type: meta.type, name: meta.label, config: clone(meta.defaultConfig), next: meta.defaultPorts.map((port) => ({ port, stepId: null })) };
  let anchor: Position | undefined;
  if (where.kind === 'after_step') {
    const from = next.steps[where.stepId];
    const link = from?.next.find((n) => n.port === where.port);
    if (!from || !link) return d;
    if (step.next[0]) step.next[0].stepId = link.stepId;
    link.stepId = id;
    anchor = below(next.layout?.[where.stepId]);
    if (step.next[0]?.stepId) shiftDown(next, step.next[0].stepId);
  } else if (where.kind === 'after_variant') {
    const variant = next.variants.find((v) => v.id === where.variantId);
    if (!variant) return d;
    if (step.next[0]) step.next[0].stepId = variant.startStepId;
    variant.startStepId = id;
    anchor = below(next.layout?.[variantKey(where.variantId)]);
    if (step.next[0]?.stepId) shiftDown(next, step.next[0].stepId);
  } else anchor = where.position;
  next.steps[id] = step;
  return withLayout(next, id, anchor);
}

/** Moves a step and everything below it one row down (room for an inserted step). */
function shiftDown(d: Draft, fromStepId: string) {
  const seen = new Set<string>();
  const walk = (id: string | null) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    const p = d.layout?.[id];
    if (p) d.layout![id] = { x: p.x, y: p.y + ROW };
    for (const n of d.steps[id]?.next ?? []) walk(n.stepId);
  };
  walk(fromStepId);
}

/** Points a port (or a variant start) at a step, or at nothing (`null` ends the journey there). */
export function connect(d: Draft, from: { stepId: string; port: string } | { variantId: string }, to: string | null): Draft {
  const next = clone(d);
  if ('variantId' in from) {
    const variant = next.variants.find((v) => v.id === from.variantId);
    if (!variant) return d;
    variant.startStepId = to;
    return next;
  }
  if (to === from.stepId) return d;
  const link = next.steps[from.stepId]?.next.find((n) => n.port === from.port);
  if (!link) return d;
  link.stepId = to;
  return next;
}

/**
 * Deletes a step and reconnects around it: what pointed at it now points at its successor when the step had a single
 * way out; after a step with branches the links end (`null`) and validation shows the gap.
 */
export function deleteStep(d: Draft, id: string): Draft {
  const step = d.steps[id];
  if (!step) return d;
  const next = clone(d);
  const successor = step.next.length === 1 ? (step.next[0]!.stepId ?? null) : null;
  delete next.steps[id];
  for (const s of Object.values(next.steps)) for (const link of s.next) if (link.stepId === id) link.stepId = successor;
  for (const v of next.variants) if (v.startStepId === id) v.startStepId = successor;
  if (next.layout) delete next.layout[id];
  return next;
}

/** A copy of a step with a new id next to it, keeping where its ports lead; nothing points at it yet. */
export function duplicateStep(d: Draft, id: string, newId: string): Draft {
  const step = d.steps[id];
  if (!step) return d;
  const next = clone(d);
  next.steps[newId] = { ...clone(step), id: newId, name: `${step.name} (copy)`.slice(0, 120) };
  const p = d.layout?.[id];
  return withLayout(next, newId, p ? { x: p.x + 260, y: p.y } : undefined);
}

export function renameStep(d: Draft, id: string, name: string): Draft {
  const trimmed = name.trim().slice(0, 120);
  if (!d.steps[id] || !trimmed || d.steps[id]!.name === trimmed) return d;
  const next = clone(d);
  next.steps[id]!.name = trimmed;
  return next;
}

/**
 * Replaces a step's settings (the step inspector, BB-115…BB-118). With `ports` — the ways out the new settings give, in
 * order — the links follow them: a surviving port keeps where it leads, a new one ends (`null`), a removed one goes.
 */
export function setStepConfig(d: Draft, id: string, config: unknown, ports?: readonly string[]): Draft {
  const step = d.steps[id];
  if (!step) return d;
  const next = ports ? [...new Set(ports)].map((port) => ({ port, stepId: step.next.find((n) => n.port === port)?.stepId ?? null })) : step.next;
  // Exact comparison: a trailing space being typed is a change (`sameContent` would drop it).
  if (JSON.stringify(step.config) === JSON.stringify(config) && JSON.stringify(step.next) === JSON.stringify(next)) return d;
  const out = clone(d);
  out.steps[id] = { ...out.steps[id]!, config: clone(config), next };
  return out;
}

export function addVariant(d: Draft, id: string): Draft {
  if (d.variants.length >= 8) return d;
  const next = clone(d);
  next.variants.push({ id, name: `Variant ${d.variants.length + 1}`, percent: 0, startStepId: null });
  return next;
}

export function renameVariant(d: Draft, id: string, name: string): Draft {
  const trimmed = name.trim().slice(0, 80);
  const next = clone(d);
  const variant = next.variants.find((v) => v.id === id);
  if (!variant || !trimmed || variant.name === trimmed) return d;
  variant.name = trimmed;
  return next;
}

export function setVariantPercent(d: Draft, id: string, percent: number): Draft {
  const next = clone(d);
  const variant = next.variants.find((v) => v.id === id);
  if (!variant) return d;
  variant.percent = Math.max(0, Math.min(100, Math.round(percent)));
  return next;
}

const reachable = (d: Draft, start: string | null): Set<string> => {
  const out = new Set<string>();
  const walk = (id: string | null) => {
    if (!id || out.has(id) || !d.steps[id]) return;
    out.add(id);
    for (const n of d.steps[id]!.next) walk(n.stepId);
  };
  walk(start);
  return out;
};

/** A copy of a variant with its whole path under new ids (Braze «Clone»); it starts with 0 %. */
export function cloneVariant(d: Draft, variantId: string, ids: IdFactory): Draft {
  const variant = d.variants.find((v) => v.id === variantId);
  if (!variant || d.variants.length >= 8) return d;
  const fragment = copyFragment(d, [...reachable(d, variant.startStepId)]);
  const { draft, map } = pasteInto(d, fragment, ids, { x: 0, y: 0 }, 300 * d.variants.length);
  draft.variants.push({ id: ids('v'), name: `${variant.name} (copy)`.slice(0, 80), percent: 0, startStepId: variant.startStepId ? (map.get(variant.startStepId) ?? null) : null });
  return draft;
}

/** Removes a variant and the steps only it reaches; the last variant cannot go. */
export function deleteVariant(d: Draft, variantId: string): Draft {
  if (d.variants.length <= 1) return d;
  const variant = d.variants.find((v) => v.id === variantId);
  if (!variant) return d;
  const others = new Set(d.variants.filter((v) => v.id !== variantId).flatMap((v) => [...reachable(d, v.startStepId)]));
  let next = clone(d);
  next.variants = next.variants.filter((v) => v.id !== variantId);
  if (next.layout) delete next.layout[variantKey(variantId)];
  for (const id of reachable(d, variant.startStepId)) if (!others.has(id)) next = deleteStep(next, id);
  return next;
}

/** Positions after a drag (one history step for the whole drag). */
export function moveNodes(d: Draft, positions: Record<string, Position>): Draft {
  const changed = Object.entries(positions).filter(([key, p]) => d.layout?.[key]?.x !== p.x || d.layout?.[key]?.y !== p.y);
  if (changed.length === 0) return d;
  return { ...d, layout: { ...(d.layout ?? {}), ...Object.fromEntries(changed.map(([k, p]) => [k, { x: Math.round(p.x), y: Math.round(p.y) }])) } };
}

/** The selected steps as a fragment: links inside it are kept, links leaving it end. */
export function copyFragment(d: Draft, ids: string[]): Fragment {
  const set = new Set(ids.filter((id) => d.steps[id]));
  const steps = [...set].map((id) => {
    const step = clone(d.steps[id]!);
    step.next = step.next.map((n) => ({ ...n, stepId: n.stepId && set.has(n.stepId) ? n.stepId : null }));
    return step;
  });
  return { steps, layout: Object.fromEntries([...set].filter((id) => d.layout?.[id]).map((id) => [id, d.layout![id]!])) };
}

function pasteInto(d: Draft, fragment: Fragment, ids: IdFactory, offset: Position, dx = 0): { draft: Draft; map: Map<string, string> } {
  const next = clone(d);
  const map = new Map(fragment.steps.map((s) => [s.id, ids('s')]));
  for (const s of fragment.steps) {
    const id = map.get(s.id)!;
    next.steps[id] = { ...clone(s), id, next: s.next.map((n) => ({ ...n, stepId: n.stepId ? (map.get(n.stepId) ?? null) : null })) };
    const p = fragment.layout[s.id];
    if (p) next.layout = { ...(next.layout ?? {}), [id]: { x: p.x + offset.x + dx, y: p.y + offset.y } };
  }
  return { draft: next, map };
}

/** Pastes a fragment (from this or another Canvas of the environment) under new ids, shifted so it does not cover the original. */
export function pasteFragment(d: Draft, fragment: Fragment, ids: IdFactory, offset: Position = { x: 40, y: 40 }): Draft {
  return pasteInto(d, fragment, ids, offset).draft;
}
